import AccountApi from "@/api/Account";
import RecipeApi from "@/api/Recipe";

import { useQueriesTyped } from "@/customHooks/useQueriesTyped";
import {
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import { AccountApiNS } from "~types/api/account";
import { RecipeApiNS } from "~types/api/recipe";
import { Folder, FolderItem } from "~types/folder";
import { UserAddress } from "~types/profile";
import { Recipe } from "~types/recipe";

import { useAuth } from "./useAuth";

import UpdateAddressArgs = AccountApiNS.UpdateAddressArgs;
import GetFoldersResponse = RecipeApiNS.GetFoldersResponse;
import GetFolderResponse = RecipeApiNS.GetFolderResponse;
import CreateFolderArgs = RecipeApiNS.CreateFolderArgs;
import AddToFolderArgs = RecipeApiNS.AddToFolderArgs;
import RemoveFromFolderArgs = RecipeApiNS.RemoveFromFolderArgs;
import GetCurrentAccountResponse = AccountApiNS.GetCurrentAccountResponse;

export enum Keys {
  Profile = "profile",
  AddressMain = "address_main",
  AddressAlt = "address_alt",
  Cart = "cart",
  CartClear = "cart_clear",
  CartInfo = "cart_info",
  FavouritesFolders = "folders",
  Notifications = "notifications",
  ReadNotification = "ReadNotification"
}

export interface Notification {
  code: number;
  created_at: string;
  id: number;
  is_viewed: boolean;
  payload: { id: number; title: string };
}

export const useProfile = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const useAccountGet = (options?: UseQueryOptions) =>
    useQuery([Keys.Profile], () => AccountApi.current(), {
      ...(options as any)
    });

  const useAccountUpdate = useMutation(
    [Keys.Profile],
    ({ data }: any) => AccountApi.updateProfileUser(data),
    {
      onMutate: async data => {
        await queryClient.cancelQueries([Keys.Profile]);
        const previousProfileData = await queryClient.getQueryData([
          Keys.Profile
        ]);
        queryClient.setQueryData(
          [Keys.Profile],
          (prev: GetCurrentAccountResponse) => ({
            ...prev,
            ...data
          })
        );
        return { previousProfileData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData([Keys.Profile], context?.previousProfileData);
        toast.error("Can't update profile. Check network connection");
      },
      onSuccess: () => {
        toast.success("Successfully update");
      }
    }
  );

  const useAddressGet = (
    addressType: UserAddress["type"],
    options?: UseQueryOptions<UserAddress>
  ) => {
    const { session } = useAuth();
    return useQuery(
      addressType ? [Keys.AddressAlt] : [Keys.AddressMain],
      () => AccountApi.getAddress({ type: addressType }),
      { ...options, disabled: !session } as UseQueryOptions<UserAddress>
    );
  };

  const useAddressUpdate = useMutation(
    (data: UpdateAddressArgs) => AccountApi.updateAddress(data),
    {
      onMutate: async data => {
        await queryClient.cancelQueries(
          data.type ? [Keys.AddressAlt] : [Keys.AddressMain]
        );
        const previousAddressData = await queryClient.getQueryData(
          data.type ? [Keys.AddressAlt] : [Keys.AddressMain]
        );
        queryClient.setQueryData(
          data.type ? [Keys.AddressAlt] : [Keys.AddressMain],
          data
        );
        return { previousAddressData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          variables.type ? [Keys.AddressAlt] : [Keys.AddressMain],
          context?.previousAddressData
        );
        toast.error("Can't update address. Check network connection");
      }
    }
  );

  const useGetFoldersShort = (
    options?: UseQueryOptions<GetFoldersResponse>
  ) => {
    const { session } = useAuth();
    return useQuery([Keys.FavouritesFolders], () => RecipeApi.getFolders(), {
      ...options,
      disabled: !session
    } as UseQueryOptions<GetFoldersResponse>);
  };

  const useGetFoldersFull = () => {
    const { session } = useAuth();
    const { data: foldersShort } = useGetFoldersShort();

    const folders = useQueriesTyped(
      foldersShort?.results?.map(folder => {
        return {
          queryKey: ["folder", folder?.pk],
          queryFn: () => RecipeApi.getFolder({ folderName: folder.name }),
          select: (data: GetFolderResponse) => ({
            ...folder,
            recipes: data.results,
            count: data.count
          }),
          disabled: !session
        };
      }) || []
    );

    return {
      data: {
        count: foldersShort?.count,
        results: folders.map(folder => folder.data)
      },
      isLoading: folders.some(folder => folder.isLoading),
      isError: folders.some(folder => folder.isError)
    };
  };

  const useCreateFolder = useMutation(
    (data: { folderName: Folder["name"] }) =>
      RecipeApi.createFolder({ name: data.folderName }),
    {
      onMutate: async data => {
        await queryClient.cancelQueries([Keys.FavouritesFolders]);
        const previousAddressData =
          await queryClient.getQueryData<GetFoldersResponse>([
            Keys.FavouritesFolders
          ]);
        queryClient.setQueryData([Keys.FavouritesFolders], prev => {
          return {
            ...previousAddressData,
            count: previousAddressData!.count + 1,
            results: [
              ...previousAddressData!.results,
              {
                pk: nanoid(),
                name: data.folderName
              }
            ]
          };
        });
        return { previousAddressData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          [Keys.FavouritesFolders],
          context?.previousAddressData
        );
        toast.error(`Can't create folder ${variables.folderName}`);
      }
    }
  );

  const useRemoveFolder = useMutation(
    (data: { folderName: Folder["name"] }) =>
      RecipeApi.removeFolder({ name: data.folderName }),
    {
      onMutate: async data => {
        await queryClient.cancelQueries([Keys.FavouritesFolders]);
        const previousAddressData =
          await queryClient.getQueryData<GetFoldersResponse>([
            Keys.FavouritesFolders
          ]);
        queryClient.setQueryData([Keys.FavouritesFolders], prev => {
          return {
            ...previousAddressData,
            count: previousAddressData!.count - 1,
            results: [
              ...previousAddressData!.results.filter(
                folder => folder.name !== data.folderName
              )
            ]
          };
        });
        return { previousAddressData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          [Keys.FavouritesFolders],
          context?.previousAddressData
        );
        toast.error(`Can't remove folder ${variables.folderName}`);
      }
    }
  );

  const useEditFolder = useMutation(
    (data: { folderId: Folder["pk"]; newFolderName: Folder["name"] }) =>
      RecipeApi.editFolder({
        folderId: data.folderId,
        newName: data.newFolderName
      }),
    {
      onMutate: async data => {
        await queryClient.cancelQueries([Keys.FavouritesFolders]);
        const previousAddressData =
          await queryClient.getQueryData<GetFoldersResponse>([
            Keys.FavouritesFolders
          ]);
        queryClient.setQueryData([Keys.FavouritesFolders], prev => {
          const folder = previousAddressData!.results.find(
            folder => folder.pk === data.folderId
          );
          return {
            ...previousAddressData,
            results: [
              ...previousAddressData!.results.filter(
                folder => folder.pk !== data.folderId
              ),
              { ...folder, name: data.newFolderName }
            ]
          };
        });
        return { previousAddressData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          [Keys.FavouritesFolders],
          context?.previousAddressData
        );
        toast.error(`Can't edit folder ${variables.folderId}`);
      }
    }
  );

  const useAddFavouriteToFolder = useMutation(
    (data: { recipeId: FolderItem["object_id"]; folderName: Folder["name"] }) =>
      RecipeApi.addToFolder({
        name: data.folderName,
        object_id: data.recipeId
      }),
    {
      onMutate: async data => {
        await queryClient.cancelQueries([Keys.FavouritesFolders]);
        const previousAddressData = await queryClient.getQueryData([
          Keys.FavouritesFolders
        ]);
        // queryClient.setQueryData([Keys.FavouritesFolders], data);
        return { previousAddressData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          [Keys.FavouritesFolders],
          context?.previousAddressData
        );
        toast.error(
          `Can't add recipe ${variables.recipeId} fo folder ${variables.folderName}`
        );
      }
    }
  );
  const useRemoveFavouriteFromFolder = useMutation(
    (data: { recipeId: FolderItem["object_id"]; folderName: Folder["name"] }) =>
      RecipeApi.removeFromFolder({
        name: data.folderName,
        object_id: data.recipeId
      }),
    {
      onMutate: async data => {
        await queryClient.cancelQueries([Keys.FavouritesFolders]);
        const previousAddressData = await queryClient.getQueryData([
          Keys.FavouritesFolders
        ]);
        queryClient.setQueryData([Keys.FavouritesFolders], data);
        return { previousAddressData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          [Keys.FavouritesFolders],
          context?.previousAddressData
        );
        toast.error(
          `Can't add recipe ${variables.recipeId} fo folder ${variables.folderName}`
        );
      }
    }
  );

  const useIsRecipeInFavourites = ({
    recipeId
  }: {
    recipeId: Recipe["pk"];
  }) => {
    const { data: fullData } = useGetFoldersFull();
    if (!fullData) {
      return false;
    }

    return fullData?.results?.some(folder =>
      folder!.recipes.some(folderItem => folderItem.object_id === recipeId)
    );
  };

  const useGetNotifications = (options?: UseQueryOptions) =>
    useQuery([Keys.Notifications], AccountApi.getNotifications, {
      ...(options as any)
    });

  const useReadNotification = useMutation(
    (id: number) => AccountApi.readNotification(id),
    {
      onMutate: async (id: number) => {
        await queryClient.cancelQueries([Keys.Notifications]);
        const previousNotifyShort = queryClient.getQueryData([
          Keys.Notifications
        ]);
        queryClient.setQueryData(
          [Keys.Notifications],
          (notifyShort: { results: Notification[] }) => {
            const newResult = notifyShort.results.map(notify =>
              notify.id === id ? { ...notify, is_viewed: true } : notify
            );

            console.log("новый результат", newResult);

            return {
              ...notifyShort,
              // count: newResults.length,
              results: newResult
            };
          }
        );
        return { previousNotifyShort };
      },
      onError: (err, newCartShort, context) => {
        queryClient.setQueryData(
          [Keys.Notifications],
          context?.previousNotifyShort
        );
        console.log(err);
        // toast.error("Can't remove product. Check network connection");
      }
    }
  );

  const useRemoveAllNotifications = useMutation(
    () => AccountApi.deleteAllNotifications(),
    {
      onMutate: async data => {
        await queryClient.cancelQueries([Keys.Notifications]);
        const previousAddressData = await queryClient.getQueryData([
          Keys.Notifications
        ]);
        queryClient.setQueryData([Keys.Notifications], []);
        return { previousAddressData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          [Keys.Notifications],
          context?.previousAddressData
        );
        toast.error("Can't clear notifications history");
      }
    }
  );

  return {
    get: useAccountGet,
    update: useAccountUpdate,
    getAddress: useAddressGet,
    updateAddress: useAddressUpdate,
    addToFolder: useAddFavouriteToFolder,
    removeFromFolder: useRemoveFavouriteFromFolder,
    isRecipeInFavourites: useIsRecipeInFavourites,
    getFoldersShort: useGetFoldersShort,
    getFoldersFull: useGetFoldersFull,
    createFolder: useCreateFolder,
    removeFolder: useRemoveFolder,
    editFolder: useEditFolder,
    getNotifications: useGetNotifications,
    clearAllNotifications: useRemoveAllNotifications,
    readNotification: useReadNotification
  };
};
